// THREE.js Animation for Login Page
export const initLoginAnimation = (containerRef, animationRef, sceneRef) => {
    // Check if THREE.js is available
    if (typeof window.THREE === 'undefined') {
        console.warn('THREE.js is not loaded. Please install it: npm install three');
        return () => { }; // Return empty cleanup function
    }

    const THREE = window.THREE;
    let renderer, scene, camera, sphereBg, nucleus, stars, controls, clock;
    let container = containerRef.current;
    let timeout_Debounce;
    let noise;
    let cameraSpeed = 0;
    let blobScale = 3;
    let delta = 0;

    // Check if SimplexNoise is available
    if (typeof window.SimplexNoise !== 'undefined') {
        noise = new window.SimplexNoise();
    } else {
        console.warn('SimplexNoise is not loaded. Some animations may not work.');
        // Create a simple fallback noise function
        noise = {
            noise3D: (x, y, z) => Math.sin(x) * Math.cos(y) * Math.sin(z) * 0.5
        };
    }

    if (!container) return () => { }; // Return empty cleanup function

    function init() {
        scene = new THREE.Scene();
        sceneRef.current = scene;

        camera = new THREE.PerspectiveCamera(
            55,
            container.clientWidth / container.clientHeight,
            0.01,
            1000
        );
        camera.position.set(0, 0, 230);

        const directionalLight = new THREE.DirectionalLight("#fff", 1);
        directionalLight.position.set(0, 50, -20);
        scene.add(directionalLight);

        let ambientLight = new THREE.AmbientLight("#ffffff", 1);
        ambientLight.position.set(0, 20, 20);
        scene.add(ambientLight);

        renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true
        });
        renderer.setSize(container.clientWidth, container.clientHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        container.appendChild(renderer.domElement);

        clock = new THREE.Clock();

        // OrbitControl - Check if OrbitControls is available
        if (typeof THREE !== 'undefined' && typeof THREE.OrbitControls !== 'undefined') {
            controls = new THREE.OrbitControls(camera, renderer.domElement);
        } else if (typeof window.OrbitControls !== 'undefined') {
            controls = new window.OrbitControls(camera, renderer.domElement);
        }

        if (controls) {
            controls.autoRotate = true;
            controls.autoRotateSpeed = 4;
            controls.maxDistance = 350;
            controls.minDistance = 150;
            controls.enablePan = false;
        }

        const loader = new THREE.TextureLoader();
        const textureSphereBg = loader.load("https://i.ibb.co/HC0vxMw/sky2.jpg");
        const texturenucleus = loader.load("https://i.ibb.co/hcN2qXk/star-nc8wkw.jpg");
        const textureStar = loader.load("https://i.ibb.co/ZKsdYSz/p1-g3zb2a.png");
        const texture1 = loader.load("https://i.ibb.co/F8by6wW/p2-b3gnym.png");
        const texture2 = loader.load("https://i.ibb.co/yYS2yx5/p3-ttfn70.png");
        const texture4 = loader.load("https://i.ibb.co/yWfKkHh/p4-avirap.png");

        /*  Nucleus  */
        texturenucleus.anisotropy = 16;
        let icosahedronGeometry = new THREE.IcosahedronGeometry(30, 10);
        let lambertMaterial = new THREE.MeshPhongMaterial({ map: texturenucleus });
        nucleus = new THREE.Mesh(icosahedronGeometry, lambertMaterial);
        scene.add(nucleus);

        /*    Sphere  Background   */
        textureSphereBg.anisotropy = 16;
        let geometrySphereBg = new THREE.SphereGeometry(150, 40, 40);
        let materialSphereBg = new THREE.MeshBasicMaterial({
            side: THREE.BackSide,
            map: textureSphereBg
        });
        sphereBg = new THREE.Mesh(geometrySphereBg, materialSphereBg);
        scene.add(sphereBg);

        /*    Moving Stars   */
        let starsGeometry = new THREE.BufferGeometry();
        let positions = [];
        let velocities = [];
        let startPositions = [];

        for (let i = 0; i < 50; i++) {
            let particleStar = randomPointSphere(150);
            positions.push(particleStar.x, particleStar.y, particleStar.z);
            let velocity = THREE.MathUtils.randInt(50, 200);
            velocities.push(velocity);
            startPositions.push(particleStar.x, particleStar.y, particleStar.z);
        }

        starsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        starsGeometry.setAttribute('velocity', new THREE.Float32BufferAttribute(velocities, 1));
        starsGeometry.setAttribute('startPosition', new THREE.Float32BufferAttribute(startPositions, 3));

        let starsMaterial = new THREE.PointsMaterial({
            size: 5,
            color: "#ffffff",
            transparent: true,
            opacity: 0.8,
            map: textureStar,
            blending: THREE.AdditiveBlending
        });
        starsMaterial.depthWrite = false;
        stars = new THREE.Points(starsGeometry, starsMaterial);
        scene.add(stars);

        /*    Fixed Stars   */
        function createStars(texture, size, total) {
            let positions = [];
            for (let i = 0; i < total; i++) {
                let radius = THREE.MathUtils.randInt(70, 149);
                let particles = randomPointSphere(radius);
                positions.push(particles.x, particles.y, particles.z);
            }
            let pointGeometry = new THREE.BufferGeometry();
            pointGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
            let pointMaterial = new THREE.PointsMaterial({
                size: size,
                map: texture,
                blending: THREE.AdditiveBlending
            });
            return new THREE.Points(pointGeometry, pointMaterial);
        }
        scene.add(createStars(texture1, 15, 20));
        scene.add(createStars(texture2, 5, 5));
        scene.add(createStars(texture4, 7, 5));

        function randomPointSphere(radius) {
            let theta = 2 * Math.PI * Math.random();
            let phi = Math.acos(2 * Math.random() - 1);
            let dx = 0 + radius * Math.sin(phi) * Math.cos(theta);
            let dy = 0 + radius * Math.sin(phi) * Math.sin(theta);
            let dz = 0 + radius * Math.cos(phi);
            return new THREE.Vector3(dx, dy, dz);
        }
    }

    // Caps the rendering loop to a specified frame interval
    function limitFPS(interval) {
        animationRef.current = requestAnimationFrame(() => limitFPS(interval));
        delta += clock.getDelta();

        if (delta > interval) {
            animate();
            delta = delta % interval;
        }
    }

    function animate() {
        if (!stars || !nucleus || !sphereBg || !renderer || !camera) return;

        // Stars Animation
        const positions = stars.geometry.attributes.position;
        const velocities = stars.geometry.attributes.velocity;
        const startPositions = stars.geometry.attributes.startPosition;

        for (let i = 0; i < positions.count; i++) {
            let x = positions.getX(i);
            let y = positions.getY(i);
            let z = positions.getZ(i);
            let velocity = velocities.getX(i);

            x += (0 - x) / velocity;
            y += (0 - y) / velocity;
            z += (0 - z) / velocity;

            velocity -= 0.3;
            velocities.setX(i, velocity);

            if (x <= 5 && x >= -5 && z <= 5 && z >= -5) {
                x = startPositions.getX(i);
                y = startPositions.getY(i);
                z = startPositions.getZ(i);
                velocity = THREE.MathUtils.randInt(50, 300);
                velocities.setX(i, velocity);
            }

            positions.setXYZ(i, x, y, z);
        }
        positions.needsUpdate = true;

        // Nucleus Animation
        const nucleusPositions = nucleus.geometry.attributes.position;
        let time = Date.now();
        for (let i = 0; i < nucleusPositions.count; i++) {
            let x = nucleusPositions.getX(i);
            let y = nucleusPositions.getY(i);
            let z = nucleusPositions.getZ(i);
            let v = new THREE.Vector3(x, y, z);
            v.normalize();
            let distance =
                nucleus.geometry.parameters.radius +
                noise.noise3D(
                    v.x + time * 0.0005,
                    v.y + time * 0.0003,
                    v.z + time * 0.0008
                ) * blobScale;
            v.multiplyScalar(distance);
            nucleusPositions.setXYZ(i, v.x, v.y, v.z);
        }
        nucleusPositions.needsUpdate = true;
        nucleus.geometry.computeVertexNormals();
        nucleus.rotation.y += 0.002;

        // Sphere Background Animation
        sphereBg.rotation.x += 0.002;
        sphereBg.rotation.y += 0.002;
        sphereBg.rotation.z += 0.002;

        if (controls) controls.update();
        renderer.render(scene, camera);
    }

    /*     Resize     */
    function onWindowResize() {
        if (!camera || !renderer || !container) return;
        camera.aspect = container.clientWidth / container.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(container.clientWidth, container.clientHeight);
    }

    const handleResize = () => {
        clearTimeout(timeout_Debounce);
        timeout_Debounce = setTimeout(onWindowResize, 80);
    };

    /*     Fullscreen btn     */
    let fullscreen = false;
    const fsEnter = document.querySelector(".fullscr");
    if (fsEnter) {
        fsEnter.addEventListener("click", function (e) {
            e.preventDefault();
            if (!fullscreen) {
                fullscreen = true;
                document.documentElement.requestFullscreen();
                fsEnter.innerHTML = "Exit Fullscreen";
            } else {
                fullscreen = false;
                document.exitFullscreen();
                fsEnter.innerHTML = "Go Fullscreen";
            }
        });
    }

    // Initialize
    init();
    limitFPS(1 / 60);
    window.addEventListener("resize", handleResize);

    // Return cleanup function
    return () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        window.removeEventListener("resize", handleResize);
        if (timeout_Debounce) {
            clearTimeout(timeout_Debounce);
        }
        if (container && renderer && renderer.domElement) {
            container.removeChild(renderer.domElement);
        }
        if (renderer) {
            renderer.dispose();
        }
        if (scene) {
            scene.traverse((object) => {
                if (object.geometry) object.geometry.dispose();
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach(material => material.dispose());
                    } else {
                        object.material.dispose();
                    }
                }
            });
        }
    };
};

